<template>
  <div class="proofs">
    <ul v-if="selfProofs.length > 0" class="proof-list">
      <li v-for="proof in selfProofs" :key="proof.id">
        <span class="fas" :class="iconOf(proof)"></span>
        <button type="button" class="name" title="Télécharger" @click="download(proof)">{{ proof.name }}</button>
        <span class="meta">{{ formatSize(proof.size) }} · {{ formatDate(proof.uploadedAt) }}</span>
        <button type="button" class="button-icon-custom" title="Retirer le justificatif" @click="remove(proof)"><span class="fas fa-xmark"></span></button>
      </li>
    </ul>
    <p v-else class="hint">Aucun justificatif : facture papier, ticket, bon de livraison...</p>

    <input ref="file" type="file" class="file-input" :accept="ACCEPTED_TYPES.join(',')" @change="onFileChosen" />
    <button type="button" class="link-button" :disabled="isUploading" @click="$refs.file.click()">{{ isUploading ? 'Envoi en cours...' : '+ Ajouter un justificatif' }}</button>
  </div>
  <!-- Hors du formulaire de l'achat : une confirmation contient son propre formulaire -->
  <Teleport to="body">
    <ConfirmModal ref="removeConfirmModal" title="Retirer le justificatif" text="Retirer ce justificatif de l'achat ? Le fichier reste conservé dans les archives de l'entreprise." yes="Retirer" no="Retour"/>
  </Teleport>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faFileImage, faFileLines, faFilePdf, faXmark } from "@fortawesome/free-solid-svg-icons";
import {NotificationService} from "@vues/v-common";
import ConfirmModal from "@/components/modals/confirmModal.vue";
import {ProofController} from "@/purchase/controllers/ProofController";
import {downloadProof} from "@/purchase/services/PurchaseDocumentService";
import {formatDate} from "@/purchase/utils";
library.add(faFileImage, faFileLines, faFilePdf, faXmark);

// Types acceptés par le serveur (qui contrôle aussi le contenu du fichier) et taille maximale
const ACCEPTED_TYPES = ["application/pdf", "image/png", "image/jpeg", "image/webp", "application/xml", "text/xml"];
const MAX_SIZE = 10 * 1024 * 1024;

// Justificatifs d'un achat enregistré : téléversement, téléchargement et retrait.
// `proofs` : justificatifs de l'achat. callback est appelé avec la nouvelle liste après chaque changement.
export default {
  name: 'PurchaseProofs',
  props: ['purchaseId', 'proofs', 'callback'],
  components: {ConfirmModal},
  data() {
    return {
      selfProofs: this.proofs ?? [],
      isUploading: false,
      ACCEPTED_TYPES,
    };
  },
  watch: {
    proofs(proofs) {
      this.selfProofs = proofs ?? [];
    },
  },
  methods: {
    formatDate,
    iconOf(proof) {
      if (proof.mimeType === "application/pdf") return "fa-file-pdf";
      return proof.mimeType.startsWith("image/") ? "fa-file-image" : "fa-file-lines";
    },
    formatSize(bytes) {
      return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} Ko` : `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} Mo`;
    },
    download(proof) {
      return downloadProof(this.purchaseId, proof);
    },
    changed() {
      if (this.callback) this.callback(this.selfProofs);
    },
    async onFileChosen(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file) return;
      if (!ACCEPTED_TYPES.includes(file.type)) return (new NotificationService).addNotification("danger", "Ce type de fichier n'est pas accepté (PDF, image ou XML)");
      if (file.size > MAX_SIZE) return (new NotificationService).addNotification("danger", "Le fichier dépasse 10 Mo");

      this.isUploading = true;
      const proof = await ProofController.upload(this.purchaseId, file);
      this.isUploading = false;
      if (proof.errors) return;

      this.selfProofs = [...this.selfProofs, proof];
      this.changed();
    },
    async remove(proof) {
      const res = await this.$refs.removeConfirmModal.displayChoice(() => ProofController.delete(this.purchaseId, proof.id));
      if (res === false || (res && res.errors)) return;

      this.selfProofs = this.selfProofs.filter(item => item.id !== proof.id);
      this.changed();
    },
  },
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/purchase/style/section.styl';

.proofs
  display flex
  flex-direction column

.file-input
  display none

.proof-list
  margin 0
  padding 0
  list-style none

  li
    display flex
    align-items center
    gap 10px
    padding 4px 0
    font-size 0.9em

    > .fas
      color #8a98b8

    .name
      min-width 0
      overflow hidden
      text-overflow ellipsis
      white-space nowrap
      padding 0
      border none
      background none
      color THEME_COLOR_PRIMARY
      font-weight 600
      cursor pointer

      &:hover
        text-decoration underline

    .meta
      margin-left auto
      color #9ca3af
      white-space nowrap
</style>
